<template>
  <div class="app-root">
    <HMTPreloader :fadeOut="ready" />
    <HMTPaymentLink />
    <HMTExitPopup v-if="getExitPopup && !getAccess.enabled" />
    <HMTLeadCapture v-if="getBranded && getLeadCapture.enabled && !getAccess.enabled" />
    <component :is="view" :data="data" @onVerifiedPassword="onVerifiedPassword($event)" />
    <notifications
      classes="form-notification"
      group="form"
      position="top center"
      width="320px"
      :duration="5000"
      :closeOnClick="true"
      :ignoreDuplicates="true"
    />
    <PortalTarget name="lightbox" multiple :data-theme="getMode"></PortalTarget>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { get } from 'lodash';
  import { isBefore } from "date-fns";
  import { toDate } from 'date-fns-tz';

  import HMTPreloader from '@/shared/components/Preloader';
  import HMTExitPopup from '@/shared/components/ExitPopup';
  import HMTLeadCapture from '@/shared/components/LeadCapture';
  import HMTPaymentLink from '@/shared/components/PaymentLink';
  import api from '@/services/api';
  import config from '@/config';
  import { getId } from '@/helpers/urlParse';
  import { mapResponseFromTourByDomain, mapResponseFromTourById } from '@/helpers/mappers';

  export default {
    name: 'App',
    components: {
      HMTPreloader,
      HMTPaymentLink,
      HMTExitPopup,
      HMTLeadCapture
    },
    data () {
      return {
        ready: false,
        success: false,
        failure: false,
        isPassword: false,
        isPortfolio: false,
        data: null
      }
    },
    computed: {
      ...mapGetters(
        'tour', [
          'getFullAddress', 
          'getBranded', 
          'getTitle', 
          'getSettings', 
          'getEnabled', 
          'getSold', 
          'getExitPopup', 
          'getLeadCapture', 
          'getFavIcon',
          'getPhotoByIndex',
          'getAccess',
          'getMode',
        ]
      ),
      isAccessByPublishDate () {
        return this.success && this.getEnabled && this.getAccess.enabled 
          && this.getAccess.isPublishDate 
          && isBefore(
            toDate(new Date(), { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }), 
            toDate(this.getAccess.publishDate, { timeZone: this.getAccess.timeZone })
          );
      },
      isAccessByPassword () {
        return this.success && this.getEnabled && this.isPassword;
      },
      // eslint-disable-next-line
      view () {
        if (this.isAccessByPassword) {
          return () => import('./views/Password');
        } else if (this.isAccessByPublishDate) {
          return () => import('./views/PublishDate');
        } else if (this.isPortfolio) {
          return () => import('./views/Portfolio');
        } else if (this.success && this.getEnabled) {
          return () => import('./LayoutProvider');
        } else if (this.success && this.getSold) {
          return () => import('@/views/Sold');
        } else if (this.failure || (this.success && !this.getEnabled && !this.getSold)) {
          return () => import('@/views/NotFound');
        }
      }
    },
    methods: {
      ...mapActions('tours', ['getTourSuccess', 'setBranded']), // Deprecated
      ...mapActions('tour', ['setTour', 'setBrandedV2']),
      fetchRequest (req, mapper) {
        const http = req(getId());

        http.then(
          response => {
            if (get(response, 'data.tour') || get(response, 'data.tourJson')) {

              // Old templates
              this.setBranded(mapper(response).branded);
              this.getTourSuccess(mapper(response).tourData);

              // New templates
              this.setBrandedV2(mapper(response).branded);
              this.setTour(mapper(response).tourData);

              if (this.getFavIcon) {
                const favicon = document.getElementById('hmt-favicon');

                favicon.setAttribute('href', this.getFavIcon);
              }

              this.isPassword = this.getAccess.enabled && this.getAccess.isTourPassword;
      
              this.success = true;
            } else {
              this.failure = true;
            }

            this.readyApp();
          }
        ).catch((error) => {
          console.log({error});

          this.failure = true;
          this.readyApp();
        });
      },
      fetchPortfolioByDomain () {
        api.tours.getPortfolioByDomain().then((response) => {
          this.data = response.data;

          this.success = true;
          this.isPortfolio = true;
          this.readyApp();
        }).catch((error) => {
          console.log({error});

          this.failure = true;
          this.readyApp();
        });
      },
      onVerifiedPassword (event) {
        this.isPassword = !event;
      },
      readyApp () {
        setTimeout(() => {
          this.ready = true;
        }, 2000);
      }
    },
    mounted () {
      if (config.DOMAIN) {
        this.fetchRequest(api.tours.getTourByDomain, mapResponseFromTourByDomain);
      } else {
        if (!getId()) {
          this.fetchPortfolioByDomain();
          return;
        }

        this.fetchRequest(api.tours.getTourById, mapResponseFromTourById);
      }
    },
    metaInfo () {
      return {
        title: this.getTitle,
        htmlAttrs: {
          lang: 'en',
          amp: true
        },
        meta: [
          {
            vmid: 'og:type',
            property: 'og:type',
            content: this.$t('meta.ogTypeWebsite')
          },
          {
            vmid: 'og:url',
            property: 'og:url',
            content: window.location.href
          },
          {
            vmid: 'og:title',
            property: 'og:title',
            content: this.getFullAddress
          },
          {
            vmid: 'og:description',
            property: 'og:description',
            content: this.$t('meta.ogDescription')
          },
          {
            vmid: 'og:image',
            property: 'og:image',
            content: get(this.getPhotoByIndex(0), 'xl')
          }
        ]
      };
    }
  };
</script>

<style lang="scss">
  html {
    &.scroll-hidden {
      overflow: hidden !important;
    }
  }

  .vue-notification-group {
    z-index: 99999999999999;

    .form-notification {
      margin: 110px 0 0;
      padding: 25px 10px;
      font-size: 15px;
      text-align: center;
      color: #fff;
      background: #44A4FC;
      border-top: 4px solid #187FE7;

      &.success {
        background: #68CD86;
        border-top-color: #42A85F;
      }

      &.warn {
        background: #ffb648;
        border-top-color: #f48a06;
      }

      &.error {
        background: #E54D42;
        border-top-color: #B82E24;
      }

      .notification-title {
        margin-bottom: 10px;
      }
    }
  }
</style>
